import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { set_page_refresh } from "../../redux/action.jsx";
import countriesData from "../../json/countries.json";
import carYearsRaw from "../../json/carsTenent/carYears.json";
import CheckboxListBox from "../../../feature-module/components/CheckboxListBox";
import { toText, byLabelAsc, sortYearsDesc } from "../../../utils/textAndSort";

const toArray = (v) => {
if (v == null || v === "") return [];
if (Array.isArray(v)) return v.filter((x) => x != null && String(x).trim() !== "");
if (typeof v === "string") return v.split(",").map((x) => x.trim()).filter(Boolean);
return [String(v)];
};

const AddTaacaaIntegration = () => {
const dispatch = useDispatch();
const MySwal = withReactContent(Swal);


const data = useSelector((s) => s.gateway_edit) || {};
const l = useSelector((s) => s.language || "en");
const t = useSelector((s) => s.translation || {});
const subcategory = useSelector((s) => s.subcategory_list || []); // makers
const dropdownList = useSelector((s) => s.dropdowns_list || []); // models
const modelList = useMemo(() => dropdownList.filter((itm) => itm.identifier === "model"), [dropdownList]);


const closeRef = useRef(null);


// Countries
const countriesOptions = useMemo(() => [
{ value: "select_country", label: t?.[l]?.["select_country"] || "Select Country" },
...countriesData.map((c) => ({ value: c.name, label: t?.[l]?.[c.name] || c.name })),
], [l, t]);



// Years -> ensure DESC by numeric value regardless of input shape
const yearsOptions = useMemo(() => {
if (!Array.isArray(carYearsRaw)) return [];
// Normalize to {id,label}
const rows = carYearsRaw[0] && typeof carYearsRaw[0] === "object" && "value" in carYearsRaw[0]
? carYearsRaw.map((o) => ({ id: toText(o.value), label: toText(o.label ?? o.value) }))
: carYearsRaw.map((y) => ({ id: toText(y), label: toText(y) }));
console.log('yearsOptions rows',rows);
return sortYearsDesc(rows);
}, []);


// Form state
const [form, setForm] = useState({ _id: "", country: "select_country", makerIds: [], makerNames: [], modelIds: [], modelNames: [], years: [] });
const [errors, setErrors] = useState({});


// Makers list
const makerItems = useMemo(() => (subcategory || []).map((m) => ({
id: toText(m._id ?? m.id ?? m.value ?? m.name),
label: toText(t?.[l]?.[m._id] ?? m._id ?? m.name ?? m.label ?? m.value),
count: typeof m.count === "number" ? m.count : undefined,
})).sort(byLabelAsc), [subcategory, l, t]);



// Model list depends on selected makers
const modelItems = useMemo(() => {
if (!form.makerIds.length) return [];
const map = new Map();
for (const mk of form.makerIds) {
(modelList || []).filter((m) => toText(m.id) === toText(mk)).forEach((itm) => {
const id = toText(itm.value ?? itm.id ?? itm.name);
if (!map.has(id)) {
map.set(id, { id, label: toText(t?.[l]?.[itm.value] ?? itm.label ?? itm.value ?? itm.name ?? id), count: typeof itm.count === "number" ? itm.count : undefined });
}
});
}
return Array.from(map.values()).sort(byLabelAsc);
}, [form.makerIds, modelList, l, t]);


// Keep models valid when makers change
useEffect(() => {
if (!form.modelIds.length) return;
const valid = new Set(modelItems.map((m) => m.id));
const nextIds = form.modelIds.filter((id) => valid.has(id));
if (nextIds.length !== form.modelIds.length) {
const nextNames = nextIds.map((id) => modelItems.find((m) => m.id === id)?.label || id);
setForm((f) => ({ ...f, modelIds: nextIds, modelNames: nextNames }));
}
}, [modelItems]);


// Reset & hydrate
const resetForm = useCallback(() => setForm({ _id: "", country: "select_country", makerIds: [], makerNames: [], modelIds: [], modelNames: [], years: [] }), []);


useEffect(() => {
if (data && Object.keys(data).length) {
setForm({
_id: data._id || "",
country: data.country || "select_country",
makerIds: toArray(data.subCategoryIds || data.subCategoryId),
makerNames: toArray(data.subCategoryNames || data.subCategoryName),
modelIds: toArray(data.modelIds || data.modelId),
modelNames: toArray(data.modelNames || data.modelName),
years: toArray(data.carYears || data.carYear).map(String),
});
} else {
resetForm();
}
}, [data, resetForm]);


useEffect(() => {
const modalEl = document.getElementById("add-brand");
const handleHidden = () => resetForm();
modalEl?.addEventListener("hidden.bs.modal", handleHidden);
return () => modalEl?.removeEventListener("hidden.bs.modal", handleHidden);
}, [resetForm]);


// Validation
const validate = useCallback(() => {
const e = {};
if (!form.country || form.country === "select_country") e.country = t?.[l]?.["country_required"] || "Country is required.";
setErrors(e);
return Object.keys(e).length === 0;
}, [form.country, l, t]);


// Submit -> keeps your existing REST path
const submit = useCallback(async (e) => {
e.preventDefault();
if (!validate()) return;
try {
await axios.post("addorupdatecardatascrapper/", {
_id: form._id || undefined,
country: form.country,
subCategoryIds: form.makerIds,
subCategoryNames: form.makerNames,
modelIds: form.modelIds,
modelNames: form.modelNames,
carYears: form.years,
// // legacy (optional) for backwards compatibility
// subCategoryId: form.makerIds.join(",") || undefined,
// subCategoryName: form.makerNames.join(",") || undefined,
// modelId: form.modelIds.join(",") || undefined,
// modelName: form.modelNames.join(",") || undefined,
// carYear: form.years.join(",") || undefined,
});


MySwal.fire({ position: "top-end", icon: "success", title: t?.[l]?.["created_success"] || "Saved successfully!", showConfirmButton: false, timer: 3000 });
resetForm();
closeRef.current?.click();
dispatch(set_page_refresh("paymentConfigs"));
} catch (error) {
console.error("Error submitting:", error);
MySwal.fire({ position: "top-end", icon: "error", title: error?.response?.data?.message || (t?.[l]?.["save_failed"] || "Save failed"), showConfirmButton: false, timer: 3000 });
}
}, [dispatch, form, l, t, MySwal, resetForm, validate]);


// Selected sets
const makerSelected = useMemo(() => new Set(form.makerIds), [form.makerIds]);
const modelSelected = useMemo(() => new Set(form.modelIds), [form.modelIds]);
const yearSelected = useMemo(() => new Set(form.years), [form.years]);



return (
<div className="modal fade" id="add-brand">
<div className="modal-dialog modal-dialog-centered custom-modal-two modal-xl">
<div className="modal-content">
<div className="page-wrapper-new p-0">
<div className="content">
<div className="modal-header border-0 custom-modal-header">
<h4>{Object.keys(data || {}).length ? (t?.[l]?.["edit_country"] || "Edit Country") : (t?.[l]?.["add_country"] || "Add Country")}</h4>
<button type="button" className="close" data-bs-dismiss="modal" aria-label="Close" ref={closeRef}><span aria-hidden="true">×</span></button>
</div>


<div className="modal-body custom-modal-body new-employee-field">
<form onSubmit={submit}>
{/* Country */}
<div className="mb-3">
<label className="form-label">{t?.[l]?.["country"] || "Country"} <span>*</span></label>
<select className="form-select" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}>
{countriesOptions.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
</select>
{errors.country && <p className="text-danger">{errors.country}</p>}
</div>


{/* Three listboxes horizontally */}
<div className="mb-3">
<div className="row g-3">
{/* MAKER */}
<div className="col-12 col-lg-4">
<CheckboxListBox
title={t?.[l]?.["makers_japanese"] || "Japanese"}
items={makerItems}
selected={makerSelected}
onChange={(next) => {
const ids = Array.from(next);
const names = ids.map((id) => makerItems.find((m) => m.id === id)?.label || id);
setForm((f) => ({ ...f, makerIds: ids, makerNames: names, modelIds: [], modelNames: [] }));
}}
/>
</div>


{/* MODEL */}
<div className="col-12 col-lg-4">
<CheckboxListBox
title={t?.[l]?.["models"] || "Models"}
items={modelItems}
selected={modelSelected}
onChange={(next) => {
const ids = Array.from(next);
const names = ids.map((id) => modelItems.find((m) => m.id === id)?.label || id);
setForm((f) => ({ ...f, modelIds: ids, modelNames: names }));
}}
footerButtons={<div className="btn-group"><button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setForm((f) => ({ ...f, modelIds: [], modelNames: [] }))}>{t?.[l]?.["clear"] || "Clear"}</button></div>}
/>
</div>


{/* YEAR */}
<div className="col-12 col-lg-4">
<CheckboxListBox
title={t?.[l]?.["years"] || "Years"}
items={yearsOptions}
selected={yearSelected}
onChange={(next) => setForm((f) => ({ ...f, years: Array.from(next) }))}
footerButtons={<div className="btn-group"><button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setForm((f) => ({ ...f, years: [] }))}>{t?.[l]?.["clear"] || "Clear"}</button></div>}
/>
</div>
</div>
</div>


<div className="modal-footer-btn">
<button type="button" className="btn btn-cancel me-2" data-bs-dismiss="modal">{t?.[l]?.["cancel"] || "Cancel"}</button>
<button type="submit" className="btn btn-submit">{t?.[l]?.["save"] || "Save"}</button>
</div>
</form>
</div>
</div>
</div>
</div>
</div>
</div>

);
};

export default AddTaacaaIntegration;

// import { useDispatch, useSelector } from "react-redux";
// import axios from "axios";
// import Swal from "sweetalert2";
// import withReactContent from "sweetalert2-react-content";
// import { set_page_refresh } from "../../redux/action.jsx";
// import countriesData from "../../json/countries.json";
// import carYearsRaw from "../../json/carsTenent/carYears.json";
// import PropTypes from "prop-types";

// /**
//  * Reusable Checkbox ListBox (scrollable, with counts) like your screenshot.
//  * - items: [{ id, label, count? }]
//  * - selected: Set<string>
//  * - onChange: (nextSet: Set<string>) => void
//  * - title: string (e.g., "Japanese")
//  * - height: px
//  * - footerButtons: optional custom footer actions
//  */
// function CheckboxListBox({
//   title,
//   items,
//   selected,
//   onChange,
//   height = 360,
//   footerButtons,
// }) {
//   const toggle = (id) => {
//     const next = new Set(selected);
//     if (next.has(id)) next.delete(id);
//     else next.add(id);
//     onChange(next);
//   };

//   const allChecked = items.length > 0 && items.every((it) => selected.has(it.id));
//   const someChecked = !allChecked && items.some((it) => selected.has(it.id));

//   const toggleAll = () => {
//     if (allChecked) onChange(new Set());
//     else onChange(new Set(items.map((i) => i.id)));
//   };

//   return (
//     <div className="listbox">
//       <div className="listbox-header">
//         <button
//           type="button"
//           className="listbox-header-toggle"
//           onClick={toggleAll}
//           aria-pressed={allChecked}
//         >
//           <span className={`checkbox ${allChecked ? "checked" : someChecked ? "indeterminate" : ""}`} />
//           <span className="listbox-title">{title}</span>
//         </button>
//       </div>

//       <div className="listbox-body" style={{ maxHeight: height }}>
//         {items.map((it) => (
//           <label key={it.id} className="listbox-item">
//             <input
//               type="checkbox"
//               checked={selected.has(it.id)}
//               onChange={() => toggle(it.id)}
//             />
//             <span className="listbox-item-label">
//               {it.label}
//               {typeof it.count === "number" ? (
//                 <span className="muted"> ({it.count})</span>
//               ) : null}
//             </span>
//           </label>
//         ))}

//         {items.length === 0 && (
//           <div className="listbox-empty">No options</div>
//         )}
//       </div>

//       {footerButtons ? <div className="listbox-footer">{footerButtons}</div> : null}
//     </div>
//   );
// }
// // --- safe label + sorter ---
// const toText = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));
// const byLabel = (a, b) =>
//   toText(a.label ?? a.id).localeCompare(toText(b.label ?? b.id), "en", { sensitivity: "base" });

// CheckboxListBox.propTypes = {
//   title: PropTypes.string.isRequired,
//   items: PropTypes.arrayOf(
//     PropTypes.shape({
//       id: PropTypes.string.isRequired,
//       label: PropTypes.string.isRequired,
//       count: PropTypes.number, // optional
//     })
//   ).isRequired,
//   selected: PropTypes.instanceOf(Set).isRequired,
//   onChange: PropTypes.func.isRequired,
//   height: PropTypes.number,
//   footerButtons: PropTypes.node,
// };

// CheckboxListBox.defaultProps = {
//   height: 360,
//   footerButtons: null,
// };


// const AddTaacaaIntegration = () => {
//   const dispatch = useDispatch();
//   const MySwal = withReactContent(Swal);

//   const data = useSelector((state) => state.gateway_edit) || {};
//   const l = useSelector((state) => state.language || "en");
//   const t = useSelector((state) => state.translation || {});
//   const subcategory = useSelector((state) => state.subcategory_list || []); // makers
//   const dropdownList = useSelector((state) => state.dropdowns_list || []);  // models
//   const modelList = dropdownList.filter((itm) => itm.identifier == "model");

//   const closeRef = useRef(null);

//   // ---------- Helpers ----------
//   const toArray = (v) => {
//     if (!v && v !== 0) return [];
//     if (Array.isArray(v)) return v;
//     if (typeof v === "string") return v.split(",").map((x) => x.trim()).filter(Boolean);
//     return [String(v)];
//   };
//   const yearsOptions = useMemo(() => {
//   if (!Array.isArray(carYearsRaw)) return [];
//   if (carYearsRaw[0] && typeof carYearsRaw[0] === "object" && "value" in carYearsRaw[0]) {
//     return carYearsRaw.map((o) => ({ id: toText(o.value), label: toText(o.label ?? o.value) })).sort(byLabel).reverse();
//   }
//   return carYearsRaw.map((y) => ({ id: toText(y), label: toText(y) })).sort(byLabel).reverse();
// }, []);


//   // ---------- State ----------
//   const [form, setForm] = useState({
//     _id: "",
//     country: "select_country",
//     makerIds: [],
//     makerNames: [],
//     modelIds: [],
//     modelNames: [],
//     years: [],
//   });
//   const [errors, setErrors] = useState({});

//   // ---------- Countries (single select via native <select>) ----------
//   const countriesOptions = useMemo(
//     () => [
//       { value: "select_country", label: t?.[l]?.["select_country"] || "Select Country" },
//       ...countriesData.map((c) => ({ value: c.name, label: t?.[l]?.[c.name] || c.name })),
//     ],
//     [l, t]
//   );

//   // ---------- Makers -> checkbox items with counts (if provided on your objects as .count) ----------
//   const makerItems = useMemo(() => {
//   return (subcategory || [])
//     .map((m) => ({
//       id: toText(m._id ?? m.id ?? m.value ?? m.name),
//       label: toText(t?.[l]?.[m._id] ?? m._id ?? m.name ?? m.label ?? m.value),
//       count: typeof m.count === "number" ? m.count : undefined,
//     }))
//     .sort(byLabel);
// }, [subcategory, l, t]);

// const modelItems = useMemo(() => {
//   if (!form.makerIds.length) return [];
//   const map = new Map();
//   for (const mk of form.makerIds) {
//     (modelList || [])
//       .filter((m) => toText(m.id) === toText(mk))
//       .forEach((itm) => {
//         const id = toText(itm.value ?? itm.id ?? itm.name);
//         if (!map.has(id)) {
//           map.set(id, {
//             id,
//             label: toText(t?.[l]?.[itm.value] ?? itm.label ?? itm.value ?? itm.name ?? id),
//             count: typeof itm.count === "number" ? itm.count : undefined,
//           });
//         }
//       });
//   }
//   return Array.from(map.values()).sort(byLabel);
// }, [form.makerIds, modelList, l, t]);

//   // ---------- Keep models valid when makers change ----------
//   useEffect(() => {
//     if (!form.modelIds.length) return;
//     const valid = new Set(modelItems.map((m) => m.id));
//     const nextIds = form.modelIds.filter((id) => valid.has(id));
//     if (nextIds.length !== form.modelIds.length) {
//       const nextNames = nextIds.map((id) => modelItems.find((m) => m.id === id)?.label || id);
//       setForm((f) => ({ ...f, modelIds: nextIds, modelNames: nextNames }));
//     }
//   }, [modelItems]); // eslint-disable-line react-hooks/exhaustive-deps

//   // ---------- Hydrate / Reset ----------
//   const resetForm = () =>
//     setForm({
//       _id: "",
//       country: "select_country",
//       makerIds: [],
//       makerNames: [],
//       modelIds: [],
//       modelNames: [],
//       years: [],
//     });

//   useEffect(() => {
//     if (data && Object.keys(data).length) {
//       setForm({
//         _id: data._id || "",
//         country: data.country || "select_country",
//         makerIds: toArray(data.subCategoryIds || data.subCategoryId),
//         makerNames: toArray(data.subCategoryNames || data.subCategoryName),
//         modelIds: toArray(data.modelIds || data.modelId),
//         modelNames: toArray(data.modelNames || data.modelName),
//         years: toArray(data.carYears || data.carYear).map(String),
//       });
//     } else {
//       resetForm();
//     }
//   }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

//   useEffect(() => {
//     const modalEl = document.getElementById("add-brand");
//     const handleHidden = () => resetForm();
//     modalEl?.addEventListener("hidden.bs.modal", handleHidden);
//     return () => modalEl?.removeEventListener("hidden.bs.modal", handleHidden);
//   }, []);

//   // ---------- Validation ----------
//   const validate = () => {
//     const e = {};
//     if (!form.country || form.country === "select_country") {
//       e.country = t?.[l]?.["country_required"] || "Country is required.";
//     }
//     setErrors(e);
//     return Object.keys(e).length === 0;
//   };

//   // ---------- Submit ----------
//   const submit = async (e) => {
//     e.preventDefault();
//     if (!validate()) return;

//     try {
//       await axios.post("addorupdatesocialmedia/", {
//         _id: form._id,
//         country: form.country,

//         // arrays (recommended)
//         subCategoryIds: form.makerIds,
//         subCategoryNames: form.makerNames,
//         modelIds: form.modelIds,
//         modelNames: form.modelNames,
//         carYears: form.years,

//         // legacy comma-joined (optional)
//         subCategoryId: form.makerIds.join(",") || undefined,
//         subCategoryName: form.makerNames.join(",") || undefined,
//         modelId: form.modelIds.join(",") || undefined,
//         modelName: form.modelNames.join(",") || undefined,
//         carYear: form.years.join(",") || undefined,
//       });

//       MySwal.fire({
//         position: "top-end",
//         icon: "success",
//         title: t?.[l]?.["created_success"] || "Saved successfully!",
//         showConfirmButton: false,
//         timer: 3000,
//       });

//       resetForm();
//       closeRef.current?.click();
//       dispatch(set_page_refresh("paymentConfigs"));
//     } catch (error) {
//       console.error("Error submitting:", error);
//       MySwal.fire({
//         position: "top-end",
//         icon: "error",
//         title: error?.response?.data?.message || (t?.[l]?.["save_failed"] || "Save failed"),
//         showConfirmButton: false,
//         timer: 3000,
//       });
//     }
//   };

//   // ---------- Derived sets for the listboxes ----------
//   const makerSelected = useMemo(() => new Set(form.makerIds), [form.makerIds]);
//   const modelSelected = useMemo(() => new Set(form.modelIds), [form.modelIds]);
//   const yearSelected  = useMemo(() => new Set(form.years),    [form.years]);

//   return (
//     <div className="modal fade" id="add-brand">
//       <div className="modal-dialog modal-dialog-centered custom-modal-two modal-xl">
//         <div className="modal-content">
//           <div className="page-wrapper-new p-0">
//             <div className="content">
//               <div className="modal-header border-0 custom-modal-header">
//                 <h4>
//                   {Object.keys(data || {}).length
//                     ? t?.[l]?.["edit_country"] || "Edit Country"
//                     : t?.[l]?.["add_country"] || "Add Country"}
//                 </h4>
//                 <button type="button" className="close" data-bs-dismiss="modal" aria-label="Close" ref={closeRef}>
//                   <span aria-hidden="true">×</span>
//                 </button>
//               </div>

//               <div className="modal-body custom-modal-body new-employee-field">
//                 <form onSubmit={submit}>
//                   {/* Country */}
//                   <div className="mb-3">
//                     <label className="form-label">
//                       {t?.[l]?.["country"] || "Country"} <span>*</span>
//                     </label>
//                     <select
//                       className="form-select"
//                       value={form.country}
//                       onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
//                     >
//                       {countriesOptions.map((o) => (
//                         <option key={o.value} value={o.value}>{o.label}</option>
//                       ))}
//                     </select>
//                     {errors.country && <p className="text-danger">{errors.country}</p>}
//                   </div>

//                   {/* Three listboxes horizontally */}
//                   <div className="mb-3">
//                     <div className="row g-3">
//                       {/* MAKER */}
//                       <div className="col-12 col-lg-4">
//                         <CheckboxListBox
//                           title={t?.[l]?.["makers_japanese"] || "Japanese"}
//                           items={makerItems}
//                           selected={makerSelected}
//                           onChange={(next) => {
//                             const ids = Array.from(next);
//                             const names = ids.map((id) => makerItems.find((m) => m.id === id)?.label || id);
//                             // Clear models when makers change; models also revalidate automatically
//                             setForm((f) => ({
//                               ...f,
//                               makerIds: ids,
//                               makerNames: names,
//                               modelIds: [],
//                               modelNames: [],
//                             }));
//                           }}
//                         />
//                       </div>

//                       {/* MODEL */}
//                       <div className="col-12 col-lg-4">
//                         <CheckboxListBox
//                           title={t?.[l]?.["models"] || "Models"}
//                           items={modelItems}
//                           selected={modelSelected}
//                           onChange={(next) => {
//                             const ids = Array.from(next);
//                             const names = ids.map((id) => modelItems.find((m) => m.id === id)?.label || id);
//                             setForm((f) => ({ ...f, modelIds: ids, modelNames: names }));
//                           }}
//                           footerButtons={
//                             <div className="btn-group">
//                               <button
//                                 type="button"
//                                 className="btn btn-sm btn-outline-secondary"
//                                 onClick={() => setForm((f) => ({ ...f, modelIds: [], modelNames: [] }))}
//                               >
//                                 {t?.[l]?.["clear"] || "Clear"}
//                               </button>
//                             </div>
//                           }
//                         />
//                       </div>

//                       {/* YEAR */}
//                       <div className="col-12 col-lg-4">
//                         <CheckboxListBox
//                           title={t?.[l]?.["years"] || "Years"}
//                           items={yearsOptions}
//                           selected={yearSelected}
//                           onChange={(next) => {
//                             const ids = Array.from(next);
//                             setForm((f) => ({ ...f, years: ids }));
//                           }}
//                           footerButtons={
//                             <div className="btn-group">
//                               <button
//                                 type="button"
//                                 className="btn btn-sm btn-outline-secondary"
//                                 onClick={() => setForm((f) => ({ ...f, years: [] }))}
//                               >
//                                 {t?.[l]?.["clear"] || "Clear"}
//                               </button>
//                             </div>
//                           }
//                         />
//                       </div>
//                     </div>
//                   </div>

//                   <div className="modal-footer-btn">
//                     <button type="button" className="btn btn-cancel me-2" data-bs-dismiss="modal">
//                       {t?.[l]?.["cancel"] || "Cancel"}
//                     </button>
//                     <button type="submit" className="btn btn-submit">
//                       {t?.[l]?.["save"] || "Save"}
//                     </button>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>     
//     </div>
//   );
// };

// export default AddTaacaaIntegration;